// Generated by Copilot- Start - 2026-04-02 17:25:39
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useState, useEffect, useRef } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvDivider from "@directv-digital/dds.ui.dtv-divider";
import DtvText from "../../../video-lab/dtv-text";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import DtvModal from "@directv-digital/dds.ui.dtv-modal";
import CardMedia from "@mui/material/CardMedia";
import ModalPackageSection from "../shared/ModalPackageSection";
import ModalPackageSectionSkeleton from "../shared/ModalPackageSectionSkeleton";
import type { PackageSummary, ChannelCardV2, SvodPromoMap } from "lib/helper/channel-interactive-tool-v2";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { fetchEnrichedShowData, needsEnrichment } from "./api.service";
import { usePackageMatching, extractDisplayNetworkName, normalizeForMatch } from "./hooks";
import { buildChannelPackages } from "../../utils/modal-package-builder";
import { buildSvodFooterText } from "../../utils/svod-footer-builder";
import { parseModalContent } from "../../utils/modal-content-parser";
import { secondsToHoursMinutes } from "./helpers";
import { dtvModalStyle, dtvModalBodyStyle, dtvModalRootSx } from "../../styles/component-styles";
import type { ShowData } from "./types";

interface MovieModalV2Props {
  open: boolean;
  clickedShow: ShowData;
  onClose: () => void;
  channelMap: Record<string, ChannelCardV2>;
  packageSummaries: Record<string, PackageSummary>;
  ovChnlPageData?: any;
  chnlSrc?: string;
  genreConfig?: any;
  theme?: any;
  /** Server-built SVOD promo map (chlupV2.svodPromoMap) keyed by channel label */
  svodPromoMap?: SvodPromoMap;
  /** Legal disclaimer text for SVOD add-on footnote (e.g. "*After 3 months, Cinemax auto-renews…") */
  addonsLegalText?: string;
  /** CMS modal content for genre/add-on labels (same shape as ChannelModalV2) */
  pkgModalContent?: any;
}

const MovieModalV2: React.FC<MovieModalV2Props> = ({
  open,
  clickedShow,
  onClose,
  channelMap,
  packageSummaries,
  ovChnlPageData = {},
  chnlSrc = "streamclup",
  genreConfig,
  theme: _theme,
  svodPromoMap = {},
  addonsLegalText,
  pkgModalContent
}) => {
  try {
    const [movieDetails, setMovieDetails] = useState<ShowData | null>(null);
    // Initialize enriching=true so the very first render always shows skeletons.
    // It stays true until the enrichment fetch (or skip) has settled and movieDetails is set.
    const [enriching, setEnriching] = useState(true);

    // Reset state whenever the modal opens with a new show so stale data from the
    // previous show never leaks through and causes packages to flash before enrichment.
    useEffect(() => {
      if (!open || !clickedShow) {
        return;
      }

      // Synchronously reset: skeleton renders on this cycle
      setMovieDetails(null);
      setEnriching(true);

      if (needsEnrichment(clickedShow)) {
        const fetchDetails = async () => {
          try {
            const enrichedData = await fetchEnrichedShowData(clickedShow);
            setMovieDetails(enrichedData || clickedShow);
          } catch (error) {
            console.error("[MovieModalV2] Error fetching movie details:", error);
            setMovieDetails(clickedShow);
          } finally {
            setEnriching(false);
          }
        };
        fetchDetails();
      } else {
        // No enrichment needed — use clickedShow directly
        setMovieDetails(clickedShow);
        setEnriching(false);
      }
    }, [clickedShow, open]);

    // isPackageDataReady: true only once:
    //  1. enrichment has settled (enriching=false)
    //  2. movieDetails is populated
    //  3. channelMap is populated (it may arrive late as a prop — this is the real source of delay)
    //  4. packageSummaries is populated (needed for denormalization)
    // All four must hold — this gates usePackageMatching and the package section render.
    const channelMapReady = channelMap && Object.keys(channelMap).length > 0;
    const packageSummariesReady = packageSummaries && Object.keys(packageSummaries).length > 0;
    const isPackageDataReady = !enriching && movieDetails !== null && channelMapReady && packageSummariesReady;

    // Use custom hook for package matching.
    // Pass null while enriching so the hook returns empty results and never runs on raw clickedShow.
    const {
      signaturePackages: rawSignaturePackages,
      genrePackages: rawGenrePackages,
      packageNetworkMap,
      matchedChannelLabels
    } = usePackageMatching(isPackageDataReady ? movieDetails : null, channelMap, packageSummaries, ovChnlPageData, chnlSrc, genreConfig);
    // Generated by Copilot- Start - 2026-04-03 20:35:47
    // True if ANY matched channel has isGenreAddonChannel=true in channelMap — gates genre add-on section and drives heading
    const hasGenreAddonChannel = matchedChannelLabels.some(
      lbl => channelMap[lbl]?.isGenreAddonChannel === true
    );
    console.log("[MovieModalV2] genre addon check", {
      matchedChannelLabels,
      hasGenreAddonChannel,
      channelMapFlags: matchedChannelLabels.map(lbl => ({
        lbl,
        isGenreAddonChannel: channelMap[lbl]?.isGenreAddonChannel
      }))
    });
    // Generated by Copilot- End - 2026-04-03 20:35:47

    const { genrePkgTxt, genrePacksLabel, alsoAvailableInTxt, availableAsAddonTxt, shopCta, genrePacksUrl, shopCtaAccessibility } = parseModalContent(pkgModalContent, false);

    // Build a movie-specific svodPromoMap slice.
    // The server svodPromoMap is keyed by channel label (e.g. "Cinemáx").
    // buildChannelPackages looks up svodPromoMap[channel.label][pkgRef.id] where
    // channel.label is the movie title (our synthetic ChannelCardV2).
    // So we merge all matched channel promo entries under the movie title key.
    const movieTitle = movieDetails?.title || "";
    // Generated by Copilot- Start - 2026-04-03 17:25:29
    const movieSvodPromoMap: SvodPromoMap = {};
    if (isPackageDataReady && matchedChannelLabels?.length > 0 && Object.keys(svodPromoMap).length > 0) {
      matchedChannelLabels.forEach(channelLabel => {
        const channelPromos = svodPromoMap[channelLabel];
        if (channelPromos && Object.keys(channelPromos).length > 0) {
          if (!movieSvodPromoMap[movieTitle]) {
            movieSvodPromoMap[movieTitle] = {};
          }
          Object.assign(movieSvodPromoMap[movieTitle], channelPromos);
        }
      });
    }
    // Generated by Copilot- End - 2026-04-03 17:25:29

    // Denormalize: rawSignaturePackages / rawGenrePackages are PackageReference[] — {id, availability}.
    // Build a synthetic ChannelCardV2 so buildChannelPackages can hydrate them into full PackageEntry[]
    // with displayLabel, packageLegal, ctaObj — exactly as ChannelModalV2, LocalChannelModalV2 etc. do.
    // Pass movieSvodPromoMap so buildChannelPackages can inject per-package SVOD promo text
    // (the same path used for regular channels in ChannelModalV2).
    const { signaturePackageList, genrePackageList } = isPackageDataReady ?
      buildChannelPackages({
        channel: {
          id: movieDetails?.resourceId || "",
          label: movieTitle,
          displayText: movieTitle,
          categoryId: movieDetails?.categories?.[0] || "",
          hasIncludedPackages: rawSignaturePackages.length > 0 || rawGenrePackages.length > 0,
          signaturePackages: rawSignaturePackages,
          genrePackages: rawGenrePackages
        } as ChannelCardV2,
        packageSummaries,
        chnlSrc,
        svodPromoMap: movieSvodPromoMap,
        isGenrePackagesEnabled: true
      }) :
      {
        signaturePackageList: [],
        genrePackageList: []
      };

    // Build SVOD footer text for legal disclaimer — same as ChannelModalV2.
    // We need to find a channel label that exists in svodPromoMap.
    // The movieSvodPromoMap is keyed by movieTitle, but the footer builder needs
    // the original channel label. Use the first matched channel label that has promos.
    const svodFooterChannelLabel = matchedChannelLabels.find(lbl => svodPromoMap[lbl] && Object.keys(svodPromoMap[lbl]).length > 0) || "";
    const formattedSvodList = buildSvodFooterText(svodFooterChannelLabel, svodPromoMap);
    const showFooter = addonsLegalText && formattedSvodList.length > 0;

    /**
     * Build the packageLegal text for a movie package entry.
     * Looks up SVOD promo text directly from svodPromoMap using matchedChannelLabels,
     * orders networks so the SVOD channel (with promo) appears last — matching prod:
     *   "Watch on TBS, TNT, Cinemax, 3 months included with new service*"
     * Falls back to plain "Watch on <networks>" when no promo applies.
     */
    // Generated by Copilot- Start - 2026-04-03 20:35:47
    /**
     * Build the packageLegal text for a movie package entry.
     * - SVOD channels (those with entries in svodPromoMap) are always ordered LAST.
     * - When a promo exists for this package, append the duration text.
     * - Display name comes from promo text ("Get Cinemax, …" → "Cinemax") or
     *   accent-stripped channel label ("Cinemáx" → "Cinemax") — whichever is available.
     *
     * Result examples:
     *   ENTERTAINMENT → "Watch on TBS, TNT, Cinemax, 3 months included with new service*"
     *   PREMIER™      → "Watch on TBS, TNT, CINEMAX"  (no promo for Premier)
     */
    const buildMoviePackageLegal = (pkgId: string): string => {
      const networks = packageNetworkMap[pkgId];
      if (!networks || networks.length === 0) {
        return "";
      }

      // Identify which matched channel labels have SVOD promo entries (for any package).
      // These channels are always placed last in the network display list.
      const svodNormalizedNames = matchedChannelLabels
        .filter(lbl => svodPromoMap[lbl] && Object.keys(svodPromoMap[lbl]).length > 0)
        .map(lbl => normalizeForMatch(lbl));

      // Separate networks: non-SVOD first, SVOD last
      const nonSvodNetworks = networks.filter(n => !svodNormalizedNames.includes(normalizeForMatch(n)));
      const svodNetworks = networks.filter(n => svodNormalizedNames.includes(normalizeForMatch(n)));

      // Find SVOD promo text for this specific package from the first eligible channel
      let promoText: string | null = null;
      let svodChannelLabel: string | null = null;
      for (const channelLabel of matchedChannelLabels) {
        const promoForPkg = svodPromoMap[channelLabel]?.[pkgId];
        if (promoForPkg) {
          promoText = promoForPkg;
          svodChannelLabel = channelLabel;
          break;
        }
      }

      // Determine display name for the SVOD channel:
      // Priority: extract from promo text ("Get Cinemax, …" → "Cinemax")
      //           then accent-strip from the channel label ("Cinemáx" → "Cinemax")
      const renamedSvodNetworks = svodNetworks.map(networkDisplayName => {
        const normalizedNet = normalizeForMatch(networkDisplayName);
        // Find which svodChannelLabel this network corresponds to
        const matchingLabel = matchedChannelLabels.find(
          lbl => svodPromoMap[lbl] && normalizeForMatch(lbl) === normalizedNet
        );
        if (!matchingLabel) {
          return networkDisplayName;
        }

        if (promoText && svodChannelLabel && normalizeForMatch(svodChannelLabel) === normalizedNet) {
          // Extract "Cinemax" from "Get Cinemax, 3 months included with new service*"
          const nameMatch = promoText.match(/^Get (.+?),/);
          if (nameMatch) {
            return nameMatch[1];
          }
        }
        // Fall back to accent-stripped label: "Cinemáx" → "Cinemax"
        return matchingLabel.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      });

      const orderedNetworks = [...nonSvodNetworks, ...renamedSvodNetworks];
      const networkDisplay = `Watch on ${orderedNetworks.join(", ")}`;

      if (promoText) {
        // Extract duration+suffix: "Get Cinemax, 3 months included with new service*"
        // → "3 months included with new service*"
        const promoMatch = promoText.match(/,\s*(\d+\s+months?\s+included\s+with\s+new\s+service\*)/i);
        if (promoMatch) {
          return `${networkDisplay}, ${promoMatch[1]}`;
        }
      }

      return networkDisplay;
    };
    // Generated by Copilot- End - 2026-04-03 20:35:47

    if (!clickedShow) {
      return null;
    }

    const show = movieDetails || clickedShow;
    const duration = show?.consumables?.[0]?.duration;
    const posterUrl = show?.images?.[0]?.imageUrl || show?.images?.[0]?.defaultImageUrl;
    const networkName = extractDisplayNetworkName(show);

    return (
      <DtvModal open={open} handleClose={onClose} modalStyle={dtvModalStyle} modalBodyStyle={dtvModalBodyStyle} sx={dtvModalRootSx}>
        <DtvBox id="movie-modal-v2">
          {/* Header with poster and title */}
          <DtvBox
            sx={{
              display: {
 xs: "block",
sm: "flex"
},
              width: "100%",
              flexWrap: "nowrap"
            }}
          >
            <CardMedia
              component="img"
              src={posterUrl}
              alt={show?.title || "Movie poster"}
              sx={{
                height: {
 xs: "156px",
md: "88px"
},
                width: {
 xs: "278px",
md: "156px"
},
                marginLeft: "15px",
                borderRadius: "8px"
              }}
              onError={e => {
                e.currentTarget.src = show?.images?.[0]?.defaultImageUrl || "";
              }}
            />

            <DtvBox sx={{
 display: "flex",
flexDirection: "column",
marginLeft: "12px",
flex: 1
}}>
              <DtvText
                variant="subhead1medium"
                component="h1"
                content={show?.title || show?.displayTitle || ""}
                sx={{
                  fontSize: "28px !important",
                  wordWrap: "break-word"
                }}
              />

              {(show?.parentalRating || show?.genres?.length > 0 || duration) && (
                <DtvText
                  content={[duration ? secondsToHoursMinutes(duration) : "", show?.parentalRating || "", show?.genres?.length > 0 ? show.genres.join(", ") : ""]
                    .filter(Boolean)
                    .join(" | ")}
                  variant="body2"
                  component="div"
                  sx={{
                    pt: "8px"
                  }}
                />
              )}
            </DtvBox>
          </DtvBox>

          <DtvDivider sx={{
 mt: {
 xs: "10px",
md: "20px"
},
mb: {
 xs: "10px",
md: "20px"
}
}} />

          {/* Package Information */}
          {!isPackageDataReady ? (
            /* Skeleton placeholders — shown while enrichment fetch + package matching are in-flight.
               Prevents the "Available as an Add-on" fallback text from flashing before real data arrives. */
            <ModalPackageSectionSkeleton itemCount={1} topMargin="0px" />
          ) : (
            <>
              {/* Signature Packages Section */}
              {/* Generated by Copilot- Start - 2026-04-03 20:35:47 */}
              <ModalPackageSection
                id="movie-package-mapping-signature"
                heading={hasGenreAddonChannel ? "Available as an Add-on" : "Available in these Signature Packages"}
                packages={signaturePackageList.map(pkg => ({
                  ...pkg,
                  packageLegal: buildMoviePackageLegal(pkg.id)
                }))}
                channelName={show?.title || ""}
                channelCategory={show?.categories?.[0] || ""}
                topMargin="0px"
              />

              {/* Genre Packs Add-on Section — ModalPackageSection with synthetic entry, same as ChannelModalV2 */}
              {hasGenreAddonChannel && (
                <ModalPackageSection
                  heading={alsoAvailableInTxt}
                  packages={[{
                    id: "genre-packs-addon",
                    displayLabel: genrePacksLabel,
                    packageLegal: availableAsAddonTxt,
                    ctaObj: {
 cta: shopCta,
ctaUrl: genrePacksUrl,
ctaAccessibility: shopCtaAccessibility
},
                    type: "genre"
                  }]}
                  channelName={show?.title || ""}
                  channelCategory={show?.categories?.[0] || ""}
                  showDivider={signaturePackageList.length > 0}
                  topMargin="26px"
                />
              )}

              {/* Genre Packages Section */}
              {genrePackageList.length > 0 && (
                <ModalPackageSection
                  id="movie-package-mapping-genre"
                  heading={genrePkgTxt}
                  packages={genrePackageList.map(pkg => ({
                    ...pkg,
                    packageLegal: buildMoviePackageLegal(pkg.id)
                  }))}
                  channelName={show?.title || ""}
                  channelCategory={show?.categories?.[0] || ""}
                  showDivider={signaturePackageList.length > 0}
                  topMargin="8px"
                />
              )}
              {/* Generated by Copilot- End - 2026-04-03 20:35:47 */}

              {/* No packages found */}
              {signaturePackageList.length === 0 && genrePackageList.length === 0 && (
                <DtvBox>
                  <DtvText
                    variant="subhead2"
                    component="span"
                    content={networkName ? `Available as an Add-on: ${networkName}` : "Package information not available"}
                    sx={{
                      fontSize: "20px !important"
                    }}
                  />
                </DtvBox>
              )}

              {/* SVOD Add-ons Legal Footer — mirrors ChannelModalV2 behaviour */}
              {showFooter && (
                <>
                  <DtvDivider sx={{
 width: "100%",
my: "16px"
}} />
                  <DtvText
                    variant="smalltext"
                    content={addonsLegalText.replace("[month], [channelName]", formattedSvodList)}
                    sx={{
                      fontSize: {
 xs: "12px !important",
sm: "14px !important"
}
                    }}
                  />
                </>
              )}
            </>
          )}
        </DtvBox>
      </DtvModal>
    );
  } catch (error) {
    console.error("Error in MovieModalV2 >> ", error);
    return <ErrorDisplay error={error} component="Movie Modal V2" />;
  }
};

export default React.memo(MovieModalV2);
// Generated by Copilot- End - 2026-04-02 17:33:47
